import { useRef, useState, useEffect } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { DialogActions, Button, CircularProgress, Box, Tooltip } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
	useGetAllInvitationsQuery,
	useReassignApplicationMutation
} from '../../services/endpoints/projectTeamEndpoints';
import Styled from './style';
import Table from '../Table/Table';
import ItemPageTitle from '../ItemPageTitle/ItemPageTitle';
import getProcessColumns from './getProcessColumns';
import usePaginatedCall from '../../hooks/usePaginatedCall';
import { getLocalAuth } from '../../utils/environmentUtils';

const ApplicationReassignmentModal = ({ isOpen, handleClose, handleAlert, refetchNavInfo }) => {
	const accountSource = useAppSelector((state) => state?.login?.accountSource);
	const apiToken = getLocalAuth(accountSource)?.access_token;
	const path = window.location.pathname;
	const pathSegments = path.split('/');
	const appID = pathSegments.find((segment) => segment.includes('api-'))?.replace('api-', '');

	const [selectedRow, setSelectedRow] = useState(null);
	const pagination = usePaginatedCall(useGetAllInvitationsQuery, {
		filterBaseName: 'INVITATIONS',
		queryParams: { acceptedOnly: true }
	});
	const owner = pagination?.rows?.results?.find((row) => row.user_role === 'Application Owner');
	const stateRef = useRef(null);
	stateRef.current = selectedRow?.team_invitation_id;
	const processColumns = getProcessColumns({ stateRef, setSelectedRow });

	const [reassignApplication, { isLoading: isReassigning }] = useReassignApplicationMutation();
	const modalContentRef = useRef(null);

	useEffect(() => {
		if (isOpen && modalContentRef.current) {
			modalContentRef.current.scrollTop = 0;
		}
	}, [isOpen, pagination]);

	const handleApplicationReassignment = () => {
		const endpointOptions = {
			apiToken: apiToken,
			appID,
			payload: {
				new_owner: selectedRow?.invitee_email
			}
		};

		reassignApplication(endpointOptions)
			.unwrap()
			.then((response) => {
				handleClose();
				handleAlert('Application owner successfully reassigned.', 'success');
				refetchNavInfo();
			})
			.catch((error) => {
				handleClose();
				handleAlert('Application owner reassignment failed.', 'error');
			});
	};

	return (
		<>
			<Styled.Dialog
				fullWidth
				maxWidth="md"
				open={isOpen}
				onClose={handleClose}
				sx={{
					'& .MuiDialog-paper': {
						maxHeight: '90vh',
						overflow: 'hidden'
					}
				}}
				aria-labelledby="responsive-dialog-title"
			>
				<Styled.DialogContent ref={modalContentRef}>
					{pagination?.isLoading ? (
						<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
							<CircularProgress />
						</Box>
					) : (
						<>
							<Styled.Head>
								<Styled.DialogTitle id="responsive-dialog-title">
									Reassign the owner of this application{' '}
								</Styled.DialogTitle>
								<Styled.DialogContentText>
									{owner
										? `${owner.invitee_email} is currently the owner of this application.`
										: ''}
								</Styled.DialogContentText>

								<Styled.DialogContentText>
									{
										'To assign a new owner, select a Team Member from the list below and click "Reassign". Please note that applications can have only one owner, and this user will be the only individual who can accept an award if your application is accepted. Only Team Members who have accepted their invite will appear in the list below.'
									}
								</Styled.DialogContentText>
							</Styled.Head>
							<ItemPageTitle text="Team members" />
							<Table
								minHeight={'400px'}
								marginTop={0}
								columns={processColumns}
								rowHeight={52}
								enableAutoPageSize
								rows={pagination?.rows?.results || []}
								getRowId={(row) => row?.team_invitation_id}
								rowCount={pagination?.rowCount + 1}
								pagination={pagination?.pagination}
								pageSize={pagination?.pageSize}
								paginationMode={pagination?.paginationMode}
								onPageChange={(e) => pagination?.onPageChange(e)}
								onPageSizeChange={(e) => pagination?.onPageSizeChange(e)}
								page={pagination?.page}
								loading={pagination?.loading}
								sortingMode={pagination?.sortingMode}
								onSortModelChange={(e) => pagination?.onSortModelChange(e)}
								hideFilters
								hideTableActions
								disableSelectionOnClick
								filterModel={pagination?.filterModel}
								sortModel={pagination?.sortModel}
							/>
							<DialogActions style={{ justifyContent: 'center' }}>
								<Button size="large" variant="outlined" onClick={handleClose} autoFocus>
									Cancel
								</Button>
								<Tooltip
									arrow
									placement="top"
									title={!selectedRow ? 'First, select a new owner' : ''}
								>
									<div>
										<LoadingButton
											size="large"
											variant="contained"
											autoFocus
											disabled={!selectedRow}
											onClick={handleApplicationReassignment}
											loading={isReassigning}
										>
											REASSIGN
										</LoadingButton>
									</div>
								</Tooltip>
							</DialogActions>
						</>
					)}
				</Styled.DialogContent>
			</Styled.Dialog>
		</>
	);
};

export default ApplicationReassignmentModal;
